/**
 * PosteingangPage — Intelligenter Dokumenten-Posteingang mit KI-Analyse
 * 
 * Features:
 * - Dokumenten-Scanner (Kamera + Upload)
 * - KI-Analyse mit OpenAI Vision (OCR + Klassifizierung)
 * - Vorschläge für Zuordnung (Firma, Akte, Fahrzeug)
 * - Aktionen (Überweisung, Ablage, Frist-Warnung)
 * - User-Validierung aller KI-Vorschläge
 * - Posteingangs-Liste mit Status
 * - Mobile-First, Micro-Minimalist Design
 */
import { useState, useCallback, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useIsMobile } from '@/hooks/useMobile';
import DocumentScanner from '@/components/DocumentScanner';
import {
  Inbox, Camera, FileText, AlertTriangle, Check, Clock, ChevronRight,
  Building2, Car, Receipt, Banknote, ArrowRight, Edit3, X, Loader2,
  Eye, Trash2, FolderInput, AlertCircle, CheckCircle2, Plus,
  CreditCard, Calendar, MapPin, User, Hash, Shield,
} from 'lucide-react';
import { toast } from 'sonner';

// ─── Typen ──────────────────────────────────────────────────────────────────
interface ScanAnalysis {
  dokumentTyp: string;
  dokumentTypLabel: string;
  absender: string;
  empfaenger: string;
  datum: string;
  betreff: string;
  zusammenfassung: string;
  firma: string;
  firmaName: string;
  aktenVerknuepfung: {
    aktenzeichen: string | null;
    kennzeichen: string | null;
    schadennummer: string | null;
    versicherung: string | null;
    gefunden: boolean;
  };
  zahlungsdaten: {
    betrag: number | null;
    waehrung: string;
    iban: string | null;
    bic: string | null;
    bankname: string | null;
    verwendungszweck: string | null;
  };
  fristen: {
    fristDatum: string | null;
    fristBeschreibung: string | null;
    fristAbgelaufen: boolean;
  };
  ablageVorschlag: {
    pfad: string;
    ordnerName: string;
  };
  aktionen: Array<{
    typ: string;
    titel: string;
    beschreibung: string;
    prioritaet: 'hoch' | 'mittel' | 'niedrig';
    betrag?: number | null;
    iban?: string | null;
  }>;
  konfidenz: number;
  extrahierterText: string;
}

interface PosteingangItem {
  id: string;
  thumbnail: string;
  analysis: ScanAnalysis | null;
  status: 'scanning' | 'analyzed' | 'validated' | 'archived' | 'error';
  createdAt: Date;
  errorMessage?: string;
}

// ─── Dokument-Typ Icons ─────────────────────────────────────────────────────
const DOK_TYP_ICONS: Record<string, any> = {
  rechnung: Receipt,
  mahnung: AlertTriangle,
  bescheid: FileText,
  zeugenfragebogen: FileText,
  vertrag: FileText,
  gutachten: FileText,
  kuerzungsschreiben: AlertCircle,
  stellungnahme: FileText,
  korrespondenz: FileText,
  bussgeld: AlertTriangle,
  steuerbescheid: Building2,
  kontoauszug: CreditCard,
  sonstiges: FileText,
};

const DOK_TYP_COLORS: Record<string, string> = {
  rechnung: '#2563EB',
  mahnung: '#DC2626',
  bescheid: '#7C3AED',
  zeugenfragebogen: '#D97706',
  bussgeld: '#DC2626',
  kuerzungsschreiben: '#DC2626',
  gutachten: '#059669',
  vertrag: '#0D9488',
  sonstiges: '#6B7280',
};

// ─── Priorität-Farben ───────────────────────────────────────────────────────
const PRIO_COLORS = {
  hoch: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
  mittel: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  niedrig: { bg: 'bg-gray-50', text: 'text-gray-600', border: 'border-gray-200' },
};

// ─── Hauptkomponente ────────────────────────────────────────────────────────
export default function PosteingangPage() {
  const isMobile = useIsMobile();
  const [items, setItems] = useState<PosteingangItem[]>([]);
  const [showScanner, setShowScanner] = useState(false);
  const [selectedItem, setSelectedItem] = useState<PosteingangItem | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  // ─── Scan abgeschlossen → KI-Analyse starten ─────────────────────────────
  // Bild komprimieren (Vercel 4.5MB Body-Limit)
  const compressImage = useCallback(async (base64: string, mimeType: string): Promise<string> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const MAX_DIM = 1600;
        let w = img.width;
        let h = img.height;
        if (w > MAX_DIM || h > MAX_DIM) {
          const ratio = Math.min(MAX_DIM / w, MAX_DIM / h);
          w = Math.round(w * ratio);
          h = Math.round(h * ratio);
        }
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0, w, h);
        const compressed = canvas.toDataURL('image/jpeg', 0.8);
        resolve(compressed.split(',')[1]);
      };
      img.onerror = () => resolve(base64);
      img.src = `data:${mimeType};base64,${base64}`;
    });
  }, []);

  const handleScanComplete = useCallback(async (scans: Array<{ image: string; mimeType: string; thumbnail: string }>) => {
    setShowScanner(false);
    setIsAnalyzing(true);

    for (const scan of scans) {
      const itemId = `scan_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const newItem: PosteingangItem = {
        id: itemId,
        thumbnail: scan.thumbnail,
        analysis: null,
        status: 'scanning',
        createdAt: new Date(),
      };

      setItems(prev => [newItem, ...prev]);

      try {
        // Bild komprimieren + API aufrufen
        const compressedImage = await compressImage(scan.image, scan.mimeType);
        const { data: { session: _scanSession } } = await supabase.auth.getSession();
        const response = await fetch('/api/scan-document', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(_scanSession?.access_token ? { Authorization: `Bearer ${_scanSession.access_token}` } : {}),
          },
          body: JSON.stringify({
            image: compressedImage,
            mimeType: 'image/jpeg',
          }),
        });

        const responseText = await response.text();
        let data;
        try {
          data = JSON.parse(responseText);
        } catch {
          throw new Error(`Server-Fehler: ${responseText.substring(0, 100)}`);
        }

        if (!response.ok) {
          throw new Error(data.error || 'Analyse fehlgeschlagen');
        }

        setItems(prev => prev.map(item =>
          item.id === itemId
            ? { ...item, analysis: data.analysis, status: 'analyzed' }
            : item
        ));

        // Automatisch auswählen
        setSelectedItem(prev => {
          if (!prev || prev.id === itemId) {
            return { ...newItem, analysis: data.analysis, status: 'analyzed' };
          }
          return prev;
        });

        // Scan in inbox_items persistieren (non-blocking)
        try {
          const ts = Date.now();
          const suffix = Math.random().toString(36).slice(2, 8);
          const storagePath = `scan/${ts}_${suffix}.jpg`;
          const imageBytes = Uint8Array.from(atob(compressedImage), (c) => c.charCodeAt(0));

          const { error: uploadError } = await supabase.storage
            .from('inbox')
            .upload(storagePath, imageBytes, { contentType: 'image/jpeg', upsert: false });

          if (!uploadError) {
            await fetch('/api/inbox', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                action: 'create',
                eingangs_quelle: 'scan',
                status: 'neu',
                storage_path: storagePath,
                mime_type: 'image/jpeg',
                dateigroesse_bytes: imageBytes.length,
                betreff: data.analysis.dokumentTypLabel,
                eingangs_datum: new Date().toISOString(),
              }),
            });
          }
        } catch {
          // Persistenz-Fehler blockiert nicht die UI
        }

        toast.success('Dokument analysiert', {
          description: data.analysis.dokumentTypLabel,
        });
      } catch (err: any) {
        console.error('Scan-Analyse Fehler:', err);
        setItems(prev => prev.map(item =>
          item.id === itemId
            ? { ...item, status: 'error', errorMessage: err.message }
            : item
        ));
        toast.error('Analyse fehlgeschlagen', { description: err.message });
      }
    }

    setIsAnalyzing(false);
  }, [compressImage]);

  // ─── Item validieren (User bestätigt) ─────────────────────────────────────
  const validateItem = useCallback((itemId: string) => {
    setItems(prev => prev.map(item =>
      item.id === itemId ? { ...item, status: 'validated' } : item
    ));
    toast.success('Dokument bestätigt und abgelegt');
  }, []);

  // ─── Item archivieren ─────────────────────────────────────────────────────
  const archiveItem = useCallback((itemId: string) => {
    setItems(prev => prev.map(item =>
      item.id === itemId ? { ...item, status: 'archived' } : item
    ));
    toast.success('Dokument archiviert');
  }, []);

  // ─── Item löschen ─────────────────────────────────────────────────────────
  const deleteItem = useCallback((itemId: string) => {
    setItems(prev => prev.filter(item => item.id !== itemId));
    if (selectedItem?.id === itemId) setSelectedItem(null);
    toast('Dokument entfernt');
  }, [selectedItem]);

  // ─── Scanner-Ansicht ──────────────────────────────────────────────────────
  if (showScanner) {
    return (
      <div className="fixed inset-0 z-50 bg-white">
        <DocumentScanner
          onScanComplete={handleScanComplete}
          onCancel={() => setShowScanner(false)}
          isAnalyzing={isAnalyzing}
        />
      </div>
    );
  }

  // ─── Detail-Ansicht (Analyse-Ergebnis) ────────────────────────────────────
  const renderDetail = (item: PosteingangItem) => {
    const a = item.analysis;
    if (!a) return null;

    const DokIcon = DOK_TYP_ICONS[a.dokumentTyp] || FileText;
    const dokColor = DOK_TYP_COLORS[a.dokumentTyp] || '#6B7280';

    return (
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200">
          {isMobile && (
            <button onClick={() => setSelectedItem(null)} className="text-gray-500 p-1">
              <X className="w-5 h-5" />
            </button>
          )}
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${dokColor}15` }}>
              <DokIcon className="w-4 h-4" style={{ color: dokColor }} />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">{a.dokumentTypLabel}</p>
              <p className="text-xs text-gray-500">{a.absender}</p>
            </div>
          </div>
          {/* Konfidenz */}
          <div className="text-xs text-gray-400 flex items-center gap-1">
            <Shield className="w-3 h-3" />
            {a.konfidenz}%
          </div>
        </div>

        {/* Inhalt */}
        <div className="flex-1 overflow-auto">
          {/* Vorschau-Bild */}
          <div className="p-3">
            <div className="rounded-xl overflow-hidden border border-gray-200 bg-gray-50 max-h-48">
              <img src={item.thumbnail} alt="Scan" className="w-full h-full object-contain" />
            </div>
          </div>

          {/* Zusammenfassung */}
          <div className="px-4 pb-3">
            <p className="text-sm text-gray-700 leading-relaxed">{a.zusammenfassung}</p>
          </div>

          {/* Erkannte Daten */}
          <div className="px-4 pb-3 space-y-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Erkannte Daten</p>

            <div className="grid grid-cols-2 gap-2">
              {a.datum && (
                <DataField icon={Calendar} label="Datum" value={a.datum} />
              )}
              {a.firmaName && (
                <DataField icon={Building2} label="Firma" value={a.firmaName} />
              )}
              {a.aktenVerknuepfung.kennzeichen && (
                <DataField icon={Car} label="Kennzeichen" value={a.aktenVerknuepfung.kennzeichen} />
              )}
              {a.aktenVerknuepfung.aktenzeichen && (
                <DataField icon={Hash} label="Aktenzeichen" value={a.aktenVerknuepfung.aktenzeichen} />
              )}
              {a.zahlungsdaten.betrag && (
                <DataField icon={Banknote} label="Betrag" value={`${a.zahlungsdaten.betrag.toFixed(2)} ${a.zahlungsdaten.waehrung}`} highlight />
              )}
              {a.zahlungsdaten.iban && (
                <DataField icon={CreditCard} label="IBAN" value={a.zahlungsdaten.iban} small />
              )}
              {a.zahlungsdaten.bankname && (
                <DataField icon={Building2} label="Bank" value={a.zahlungsdaten.bankname} />
              )}
              {a.aktenVerknuepfung.versicherung && (
                <DataField icon={Shield} label="Versicherung" value={a.aktenVerknuepfung.versicherung} />
              )}
            </div>
          </div>

          {/* Frist-Warnung */}
          {a.fristen.fristDatum && (
            <div className={`mx-4 mb-3 p-3 rounded-xl border ${a.fristen.fristAbgelaufen ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
              <div className="flex items-center gap-2">
                <AlertTriangle className={`w-4 h-4 ${a.fristen.fristAbgelaufen ? 'text-red-600' : 'text-amber-600'}`} />
                <span className={`text-sm font-medium ${a.fristen.fristAbgelaufen ? 'text-red-700' : 'text-amber-700'}`}>
                  {a.fristen.fristAbgelaufen ? 'Frist abgelaufen!' : 'Frist beachten'}
                </span>
              </div>
              <p className={`text-xs mt-1 ${a.fristen.fristAbgelaufen ? 'text-red-600' : 'text-amber-600'}`}>
                {a.fristen.fristBeschreibung} ({a.fristen.fristDatum})
              </p>
            </div>
          )}

          {/* Ablage-Vorschlag */}
          <div className="mx-4 mb-3 p-3 rounded-xl bg-[#0D9488]/5 border border-[#0D9488]/20">
            <div className="flex items-center gap-2 mb-1">
              <FolderInput className="w-4 h-4 text-[#0D9488]" />
              <span className="text-xs font-semibold text-[#0D9488] uppercase tracking-wider">Ablage-Vorschlag</span>
            </div>
            <p className="text-sm text-gray-700">{a.ablageVorschlag.pfad}</p>
          </div>

          {/* Aktionen */}
          {a.aktionen.length > 0 && (
            <div className="px-4 pb-3 space-y-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Vorgeschlagene Aktionen</p>
              {a.aktionen.map((aktion, i) => {
                const prio = PRIO_COLORS[aktion.prioritaet];
                return (
                  <button
                    key={i}
                    className={`w-full text-left p-3 rounded-xl border ${prio.border} ${prio.bg} hover:opacity-90 transition-opacity`}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`text-sm font-medium ${prio.text}`}>{aktion.titel}</span>
                      <ChevronRight className={`w-4 h-4 ${prio.text}`} />
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">{aktion.beschreibung}</p>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer — Bestätigen / Bearbeiten */}
        {item.status === 'analyzed' && (
          <div className="px-4 py-3 border-t border-gray-200 bg-white flex gap-2">
            <button
              onClick={() => validateItem(item.id)}
              className="flex-1 flex items-center justify-center gap-2 py-3 bg-[#0D9488] text-white rounded-xl font-medium hover:bg-[#0B8278] active:scale-[0.98] transition-all"
            >
              <Check className="w-4 h-4" />
              Bestätigen & Ablegen
            </button>
            <button
              onClick={() => deleteItem(item.id)}
              className="px-4 py-3 bg-gray-100 text-gray-600 rounded-xl hover:bg-gray-200 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    );
  };

  // ─── Hauptansicht ─────────────────────────────────────────────────────────
  const pendingItems = items.filter(i => i.status === 'analyzed');
  const validatedItems = items.filter(i => i.status === 'validated' || i.status === 'archived');
  const scanningItems = items.filter(i => i.status === 'scanning');

  // Mobile: Detail-Ansicht oder Liste
  if (isMobile && selectedItem) {
    return (
      <div className="h-full bg-white">
        {renderDetail(selectedItem)}
      </div>
    );
  }

  return (
    <div className="h-full flex bg-gray-50">
      {/* Linke Seite: Liste */}
      <div className={`${isMobile ? 'w-full' : selectedItem ? 'w-[380px] border-r border-gray-200' : 'w-full'} flex flex-col bg-white`}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Inbox className="w-5 h-5 text-[#0D9488]" />
            <h1 className="text-lg font-semibold text-gray-900">Kommunikationseingang</h1>
            {pendingItems.length > 0 && (
              <span className="px-2 py-0.5 bg-[#0D9488] text-white text-xs font-medium rounded-full">
                {pendingItems.length}
              </span>
            )}
          </div>
          <button
            onClick={() => setShowScanner(true)}
            className="flex items-center gap-1.5 px-3 py-2 bg-[#0D9488] text-white text-sm font-medium rounded-lg hover:bg-[#0B8278] active:scale-[0.98] transition-all"
          >
            <Camera className="w-4 h-4" />
            Scannen
          </button>
        </div>

        {/* Liste */}
        <div className="flex-1 overflow-auto">
          {items.length === 0 ? (
            /* Leerer Zustand */
            <div className="flex flex-col items-center justify-center h-full gap-4 p-8">
              <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center">
                <Inbox className="w-8 h-8 text-gray-300" />
              </div>
              <div className="text-center">
                <p className="text-base font-medium text-gray-900">Posteingang leer</p>
                <p className="text-sm text-gray-500 mt-1">Scanne ein Dokument, um zu starten</p>
              </div>
              <button
                onClick={() => setShowScanner(true)}
                className="flex items-center gap-2 px-5 py-3 bg-[#0D9488] text-white rounded-xl font-medium hover:bg-[#0B8278] active:scale-[0.98] transition-all"
              >
                <Camera className="w-5 h-5" />
                Dokument scannen
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {/* Scanning Items */}
              {scanningItems.map(item => (
                <div key={item.id} className="flex items-center gap-3 px-4 py-3 bg-blue-50/50">
                  <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                    <img src={item.thumbnail} alt="" className="w-full h-full object-cover opacity-60" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                      <span className="text-sm font-medium text-blue-700">KI analysiert...</span>
                    </div>
                  </div>
                </div>
              ))}

              {/* Analysierte Items */}
              {pendingItems.map(item => {
                const a = item.analysis!;
                const DokIcon = DOK_TYP_ICONS[a.dokumentTyp] || FileText;
                const dokColor = DOK_TYP_COLORS[a.dokumentTyp] || '#6B7280';
                const isSelected = selectedItem?.id === item.id;

                return (
                  <button
                    key={item.id}
                    onClick={() => setSelectedItem(item)}
                    className={`w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors ${isSelected ? 'bg-[#0D9488]/5 border-l-2 border-[#0D9488]' : ''}`}
                  >
                    <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                      <img src={item.thumbnail} alt="" className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <DokIcon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: dokColor }} />
                        <span className="text-sm font-medium text-gray-900 truncate">{a.dokumentTypLabel}</span>
                      </div>
                      <p className="text-xs text-gray-500 truncate mt-0.5">{a.absender}</p>
                      {a.zahlungsdaten.betrag && (
                        <p className="text-xs font-medium text-gray-700 mt-0.5">
                          {a.zahlungsdaten.betrag.toFixed(2)} {a.zahlungsdaten.waehrung}
                        </p>
                      )}
                    </div>
                    {a.fristen.fristAbgelaufen && (
                      <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    )}
                    <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
                  </button>
                );
              })}

              {/* Validierte Items */}
              {validatedItems.length > 0 && (
                <>
                  <div className="px-4 py-2 bg-gray-50">
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Erledigt</span>
                  </div>
                  {validatedItems.map(item => {
                    const a = item.analysis;
                    return (
                      <div key={item.id} className="flex items-center gap-3 px-4 py-3 opacity-60">
                        <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                          <img src={item.thumbnail} alt="" className="w-full h-full object-cover" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-600 truncate">{a?.dokumentTypLabel || 'Dokument'}</p>
                          <p className="text-xs text-gray-400 truncate">{a?.absender || ''}</p>
                        </div>
                        <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Rechte Seite: Detail (nur Desktop) */}
      {!isMobile && selectedItem && (
        <div className="flex-1 bg-white">
          {renderDetail(selectedItem)}
        </div>
      )}

      {/* Rechte Seite: Leer-Zustand (Desktop) */}
      {!isMobile && !selectedItem && items.length > 0 && (
        <div className="flex-1 flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <Eye className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-400">Dokument auswählen</p>
          </div>
        </div>
      )}

      {/* Floating Action Button (Mobile) — über der Bottom-Navigation */}
      {isMobile && !showScanner && (
        <button
          onClick={() => setShowScanner(true)}
          className="fixed right-5 w-14 h-14 bg-[#0D9488] text-white rounded-full shadow-lg flex items-center justify-center hover:bg-[#0B8278] active:scale-95 transition-all z-40"
          style={{ bottom: 'calc(5rem + env(safe-area-inset-bottom, 0px))' }}
        >
          <Camera className="w-6 h-6" />
        </button>
      )}
    </div>
  );
}

// ─── Hilfskomponente: Datenfeld ─────────────────────────────────────────────
function DataField({ icon: Icon, label, value, highlight, small }: {
  icon: any;
  label: string;
  value: string;
  highlight?: boolean;
  small?: boolean;
}) {
  return (
    <div className={`flex items-start gap-2 p-2 rounded-lg ${highlight ? 'bg-amber-50' : 'bg-gray-50'}`}>
      <Icon className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${highlight ? 'text-amber-600' : 'text-gray-400'}`} />
      <div className="min-w-0">
        <p className="text-[10px] text-gray-400 uppercase tracking-wider">{label}</p>
        <p className={`${small ? 'text-[11px]' : 'text-xs'} font-medium ${highlight ? 'text-amber-700' : 'text-gray-700'} break-all`}>
          {value}
        </p>
      </div>
    </div>
  );
}
